import StatusPopover from '../../../../components/v2/StatusPopover.jsx';
import { Link } from 'react-router-dom';
import FilterPopover from '../../../../components/v2/FilterPopover.jsx';
import { relativeTimeFromUtc } from '../../../../utils/campusTime.js';
import { ariaSort } from '../../../../components/v2/SortableTh.jsx';

/**
 * Normalizes a SQL-style "YYYY-MM-DD HH:MM:SS" string (which browsers
 * otherwise parse as local time) to a Z-suffixed ISO string so it parses
 * as UTC. Pass-through if the string already has a timezone marker.
 */
function normalizeAsUtc(isoString) {
  if (!isoString) return '';
  const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(isoString);
  return hasTz ? isoString : isoString.replace(' ', 'T') + 'Z';
}

/**
 * Full datetime for the popover on the UTC column \u2014 renders the UTC moment
 * in the user's local timezone (the browser's locale formatter handles the
 * conversion once we've parsed the source as UTC).
 */
function fullDateTimeUtc(isoString) {
  if (!isoString) return '';
  const d = new Date(normalizeAsUtc(isoString));
  if (Number.isNaN(d.getTime())) return isoString;
  return d.toLocaleString();
}

/**
 * Formats a string that is already in the campus's LOCAL timezone \u2014 render
 * it as-is in MM/DD/YYYY h:mm AM/PM. We parse via UTC normalization and read
 * back with getUTC* accessors so the browser's own tz doesn't shift the
 * wall-clock value the campus actually saw.
 */
function formatLocalTimestamp(isoString) {
  if (!isoString) return '\u2014';
  const d = new Date(normalizeAsUtc(isoString));
  if (Number.isNaN(d.getTime())) return isoString;
  const pad = (n) => String(n).padStart(2, '0');
  const mm = pad(d.getUTCMonth() + 1);
  const dd = pad(d.getUTCDate());
  const yyyy = d.getUTCFullYear();
  let hh = d.getUTCHours();
  const min = pad(d.getUTCMinutes());
  const ampm = hh >= 12 ? 'PM' : 'AM';
  hh = hh % 12 || 12;
  return `${mm}/${dd}/${yyyy} ${hh}:${min} ${ampm}`;
}

// ---------------------------------------------------------------------------
// Column definitions — 6 columns (removed alarm bytes raw/parsed from V1's 8)
// ---------------------------------------------------------------------------
export const ALL_SERVERS_COLUMN_DEFS = [
  { key: 'campus_name', label: 'Campus Name', sortable: true, filterable: true },
  { key: 'corporate_name', label: 'Corporation', sortable: true, filterable: true },
  { key: 'checkin_time', label: 'Checkin Time (UTC)', sortable: true, filterable: false },
  { key: 'recent_alarm_input_time', label: 'Recent Alarm Time (Local)', sortable: true, filterable: false },
  { key: 'version_database', label: 'DB Version', sortable: true, filterable: true },
  { key: 'version_code', label: 'Code Version', sortable: true, filterable: true },
];

/**
 * AllServersTable — read-only data table for cross-campus PalCare servers
 * with sorting, filtering, and sticky header. No checkboxes or row actions.
 */
const AllServersTable = ({
  data,
  allData,
  visibleColumns,
  sortColumn,
  sortOrder,
  onSort,
  filters,
  onFilterChange,
}) => {
  const activeColumns = ALL_SERVERS_COLUMN_DEFS.filter((col) =>
    visibleColumns.includes(col.key)
  );

  /** Collect unique filter values from the full (unfiltered) dataset */
  const getFilterValues = (columnKey) => {
    const values = new Set();
    (allData || data).forEach((row) => {
      const val = row[columnKey];
      values.add(val != null && val !== '' ? String(val) : '(empty)');
    });
    return [...values].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );
  };

  const renderSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return (
        <svg
          className="dp-icon-sm dp-sort-icon dp-sort-icon--neutral"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
          />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg
        className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4.5 15.75l7.5-7.5 7.5 7.5"
        />
      </svg>
    ) : (
      <svg
        className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  };

  /** Renders a single cell value */
  const renderCell = (row, columnKey) => {
    switch (columnKey) {
      case 'campus_name':
        return row.campus_name && row.campus_id ? (
          <Link
            to={'/profiles/campus/' + row.campus_id}
            className="dp-device-link"
            style={{ fontWeight: 'var(--font-weight-semibold)' }}
          >
            {row.campus_name}
          </Link>
        ) : (
          <span className="dp-cell-text" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
            {row.campus_name || '\u2014'}
          </span>
        );

      case 'corporate_name':
        return row.corporate_name && row.corporate_id ? (
          <Link
            to={'/profiles/corporate/' + row.corporate_id}
            className="dp-device-link"
          >
            {row.corporate_name}
          </Link>
        ) : (
          <span className="dp-cell-text--muted">&mdash;</span>
        );

      case 'checkin_time':
        return row.checkin_time ? (
          <StatusPopover message={fullDateTimeUtc(row.checkin_time)}>
            <span className="dp-cell-text dp-cell-text--secondary">
              {relativeTimeFromUtc(row.checkin_time)}
            </span>
          </StatusPopover>
        ) : (
          <span className="dp-cell-text--muted">&mdash;</span>
        );

      case 'recent_alarm_input_time':
        return row.recent_alarm_input_time ? (
          <span className="dp-cell-text dp-cell-text--secondary">
            {formatLocalTimestamp(row.recent_alarm_input_time)}
          </span>
        ) : (
          <span className="dp-cell-text--muted">&mdash;</span>
        );

      default:
        return (
          <span className="dp-cell-text">{row[columnKey] || '\u2014'}</span>
        );
    }
  };

  return (
    <div className="dp-table-wrap">
      <table className="dp-table">
        <thead className="dp-thead">
          <tr>
            {/* Data columns */}
            {activeColumns.map((col) => (
              <th key={col.key} scope="col" className="dp-th" aria-sort={col.sortable ? ariaSort(sortColumn, sortOrder, col.key) : undefined}>
                <div className="dp-th__content">
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key)}
                      className="dp-th__sort-btn"
                      aria-label={`Sort by ${col.label}`}
                    >
                      {col.label}
                      {renderSortIcon(col.key)}
                    </button>
                  ) : (
                    <span>{col.label}</span>
                  )}

                  {col.filterable ? (
                    <FilterPopover
                      columnKey={col.filterKey || col.key}
                      label={col.label}
                      values={getFilterValues(col.filterKey || col.key)}
                      selectedValues={filters[col.filterKey || col.key] || []}
                      onChange={onFilterChange}
                      data={allData}
                    />
                  ) : null}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="dp-tbody">
          {data.map((row, idx) => (
            <tr key={`${row.campus_id}-${idx}`} className="dp-tr">
              {/* Data cells */}
              {activeColumns.map((col) => (
                <td key={col.key} className="dp-td">
                  {renderCell(row, col.key)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AllServersTable;
